import React, { useState, useEffect, useRef, useCallback } from 'react'
import {
  Form,
  Input,
  Select,
  DatePicker,
  Button,
  Card,
  message,
  InputNumber,
  Space,
  Breadcrumb,
  Empty,
  Row,
  Col,
  Tag,
  Typography
} from 'antd'
import {
  PlusOutlined,
  DeleteOutlined,
  SaveOutlined,
  ShoppingOutlined,
  HomeOutlined,
  FilterOutlined
} from '@ant-design/icons'
import styled from 'styled-components'
import { useAuth } from '../hooks/AuthContext'
import { useMembership } from '../hooks/useMembership'
import { usePermission } from '../hooks/usePermission'
import { useNavigate } from 'react-router-dom'
import {
  getInventoryProducts,
  getInventoryCategories,
  createInventoryUsage
} from '../api/inventory'
import dayjs from 'dayjs'
import { useMediaQuery } from 'react-responsive'
import { now as tzNow } from '../utils/timezone'

const { useWatch } = Form
const { Option } = Select
const { TextArea } = Input
const { Text } = Typography

/** 商品「容量」欄位：用於拆庫存時標示每 1 計量單位對應的規格（如 500ml） */
const formatPerUnitSpec = (product) => {
  if (!product) return ''
  const raw = product.capacity
  const cap = typeof raw === 'string' ? raw.trim() : raw != null ? String(raw).trim() : ''
  if (!cap) return ''
  const u = product.unit || '件'
  return `每 1${u}：${cap}`
}

const PageContainer = styled.div`
  padding: 24px;
  background: #f5f5f5;
  min-height: 100vh;

  @media (max-width: 768px) {
    padding: 16px;
  }
`

const PageHeader = styled.div`
  margin-bottom: 24px;
`

const PageTitle = styled.h1`
  font-size: 28px;
  font-weight: 600;
  color: #1a1a1a;
  margin: 0 0 16px 0;

  @media (max-width: 768px) {
    font-size: 24px;
  }
`

const FormCard = styled(Card)`
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  border: none;
  margin-bottom: 24px;

  .ant-card-head {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    border-bottom: none;
    border-radius: 12px 12px 0 0;

    .ant-card-head-title {
      color: white;
      font-size: 18px;
      font-weight: 600;
    }
  }

  .ant-card-body {
    padding: 24px;

    @media (max-width: 768px) {
      padding: 16px;
    }
  }
`

const ProductItemCard = styled.div`
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 16px;
  position: relative;
  transition: all 0.2s ease;
  overflow: visible;

  &:hover {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    border-color: #d1d5db;
  }

  /* 確保表單元素不會阻擋刪除按鈕 */
  .ant-form-item {
    margin-bottom: 16px;
  }

  /* 確保表單控件可以正常交互 */
  .ant-select,
  .ant-input-number {
    pointer-events: auto;
    position: relative;
  }

  /* 確保表單控件不被遮擋 */
  .ant-select-selector,
  .ant-input-number-input {
    cursor: pointer;
  }
`

const DeleteButton = styled(Button)`
  position: absolute;
  top: 12px;
  right: 12px;
  color: #ef4444;
  border: none;
  padding: 4px;
  min-width: auto;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;
  cursor: pointer;

  &:hover {
    color: #dc2626;
    background: rgba(239, 68, 68, 0.1);
  }
`

const FormRow = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`

const SummaryCard = styled(Card)`
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  border: none;
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  margin-bottom: 24px;

  .ant-card-body {
    padding: 24px;

    @media (max-width: 768px) {
      padding: 16px;
    }
  }

  .summary-title {
    color: rgba(255, 255, 255, 0.9);
    font-size: 14px;
    font-weight: 500;
    margin-bottom: 8px;
  }

  .summary-value {
    color: #fff;
    font-size: 32px;
    font-weight: 700;

    @media (max-width: 768px) {
      font-size: 24px;
    }
  }
`

const ButtonGroup = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 24px;
  padding-top: 24px;
  border-top: 1px solid #e5e7eb;

  @media (max-width: 768px) {
    flex-direction: column;

    button {
      width: 100%;
    }
  }
`

// ProductItemForm 必須定義在父元件之外，否則每次父元件 re-render 都會建立新的函式
// 參考，React 會把它當成「不同元件」→ 卸載再重掛 → 內部 form / DOM 跟著重建，
// 使用者剛輸入到一半就被打斷（焦點丟失、欄位被重置）。提到外面後 React 會用 key
// 認得是同一個 instance，state 才會穩定。
const ProductItemForm = ({
  item,
  products,
  visibleProducts,
  categories = [],
  handleProductChange,
  handleRemoveProduct,
  productFormsRef,
  canManageInventory
}) => {
  const [itemForm] = Form.useForm()
  const selectedProductId = useWatch('productId', itemForm)
  const quantity = useWatch('quantity', itemForm)
  const cost = useWatch('cost', itemForm)
  const lastProcessedProductIdRef = useRef(null)
  const lastProcessedQuantityRef = useRef(null)
  const lastProcessedCostRef = useRef(null)

  // 商品列表用的「真資料」直接從 parent state 的 item.productId 推導，這樣即使
  // form 還沒同步完成（剛 mount、useWatch 還沒 settle）也能正確顯示／不被誤 disable。
  const selectedProduct = products.find(
    (p) =>
      p._id === (selectedProductId || item.productId) ||
      p.id === (selectedProductId || item.productId)
  )
  const selectedPerUnitSpec = formatPerUnitSpec(selectedProduct)
  const productSelected = !!(selectedProductId || item.productId)

  productFormsRef.current[item.key] = itemForm

  // 初始化表單值（只在元件首次掛載時跑）
  useEffect(() => {
    if (itemForm) {
      const initQty =
        item.quantity === null || item.quantity === undefined
          ? undefined
          : item.quantity
      itemForm.setFieldsValue({
        productId: item.productId || undefined,
        quantity: initQty,
        cost: item.cost ?? 0,
        totalCost: item.totalCost ?? 0
      })
      lastProcessedProductIdRef.current = item.productId || null
      lastProcessedQuantityRef.current =
        initQty === undefined ? null : initQty
      lastProcessedCostRef.current = item.cost ?? 0
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    return () => {
      delete productFormsRef.current[item.key]
    }
  }, [item.key, productFormsRef])

  // 當選擇商品時，自動填充成本和重置數量
  useEffect(() => {
    if (
      !selectedProductId ||
      selectedProductId === lastProcessedProductIdRef.current
    ) {
      return
    }
    if (products.length === 0) return

    const product = products.find(
      (p) => p._id === selectedProductId || p.id === selectedProductId
    )
    if (!product) return

    const productCost = product.cost || 0
    const maxStock = product.stock || 0
    const defaultQuantity = maxStock > 0 ? 1 : 0

    lastProcessedProductIdRef.current = selectedProductId
    lastProcessedQuantityRef.current = defaultQuantity
    lastProcessedCostRef.current = productCost

    const initialTotalCost = defaultQuantity * productCost

    itemForm.setFieldsValue({
      cost: productCost,
      quantity: defaultQuantity,
      totalCost: initialTotalCost
    })

    handleProductChange(item.key, 'productId', selectedProductId)
    handleProductChange(
      item.key,
      'productName',
      product.name || product.title
    )
    handleProductChange(item.key, 'cost', productCost)
    handleProductChange(item.key, 'unit', product.unit || '件')
    handleProductChange(item.key, 'stock', maxStock)
    handleProductChange(item.key, 'quantity', defaultQuantity)
    handleProductChange(item.key, 'totalCost', initialTotalCost)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProductId])

  // 當數量或成本改變時，更新總成本
  useEffect(() => {
    const currentQuantity = itemForm.getFieldValue('quantity')
    const currentCost = itemForm.getFieldValue('cost') || 0

    const validQuantity = Number(currentQuantity) || 0
    const validCost = Number(currentCost) || 0

    if (
      lastProcessedQuantityRef.current !== null &&
      lastProcessedCostRef.current !== null &&
      validQuantity === lastProcessedQuantityRef.current &&
      validCost === lastProcessedCostRef.current
    ) {
      return
    }

    if (validQuantity <= 0 || validCost < 0) {
      const totalCost = 0
      itemForm.setFieldsValue({ totalCost })
      handleProductChange(item.key, 'totalCost', totalCost)
      lastProcessedQuantityRef.current = validQuantity
      lastProcessedCostRef.current = validCost
      return
    }

    const totalCost = validQuantity * validCost
    const currentTotalCost = itemForm.getFieldValue('totalCost') || 0
    if (Math.abs(currentTotalCost - totalCost) > 0.01) {
      lastProcessedQuantityRef.current = validQuantity
      lastProcessedCostRef.current = validCost
      itemForm.setFieldsValue({ totalCost })
      handleProductChange(item.key, 'totalCost', totalCost)
    } else {
      lastProcessedQuantityRef.current = validQuantity
      lastProcessedCostRef.current = validCost
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quantity, cost])

  // 來源：父層已套用「分類／關鍵字」篩選後的商品（visibleProducts），
  // 再保留拆公用頁原本的「啟用 + 有庫存」條件；
  // 並永遠保留目前列已選的商品，避免切換篩選後選項消失。
  const baseList = Array.isArray(visibleProducts) ? visibleProducts : products
  const currentSelectedId = selectedProductId || item.productId
  const filteredProducts = baseList.filter(
    (p) => p.status === 'active' && (p.stock || 0) > 0
  )
  if (
    currentSelectedId &&
    !filteredProducts.find(
      (p) => p._id === currentSelectedId || p.id === currentSelectedId
    )
  ) {
    const fallback = products.find(
      (p) => p._id === currentSelectedId || p.id === currentSelectedId
    )
    if (fallback) filteredProducts.unshift(fallback)
  }

  // 取得商品分類名稱（用於下拉選項右側標籤）
  const getCategoryName = (product) => {
    if (product?.categoryName) return product.categoryName
    if (product?.category?.name) return product.category.name
    const cid =
      product?.categoryId || product?.category?.id || product?.category?._id
    if (cid) {
      const cat = categories.find((c) => c._id === cid)
      if (cat?.name) return cat.name
    }
    return ''
  }

  return (
    <ProductItemCard>
      <DeleteButton
        type='text'
        danger
        icon={<DeleteOutlined />}
        onClick={(e) => {
          e.stopPropagation()
          e.preventDefault()
          handleRemoveProduct(item.key)
        }}
      />
      <Form form={itemForm} layout='vertical'>
        <FormRow>
          <Form.Item
            name='productId'
            label='商品'
            rules={[{ required: true, message: '請選擇商品' }]}
          >
            <Select
              placeholder='選擇商品'
              size='large'
              showSearch
              optionFilterProp='label'
              getPopupContainer={() => document.body}
              dropdownStyle={{ zIndex: 1050 }}
              notFoundContent={
                products.length === 0
                  ? '尚未建立任何商品'
                  : '此分類／關鍵字下沒有可用的商品'
              }
              onChange={(value) => {
                handleProductChange(item.key, 'productId', value)
              }}
            >
              {filteredProducts.map((product) => {
                const spec = formatPerUnitSpec(product)
                const stockText =
                  product.stock !== undefined
                    ? `(庫存: ${product.stock}${product.unit || '件'})`
                    : ''
                const baseLabel = `${product.name || product.title} ${stockText}${
                  spec ? `｜${spec}` : ''
                }`.trim()
                const categoryName = getCategoryName(product)
                return (
                  <Option
                    key={product._id || product.id}
                    value={product._id || product.id}
                    label={baseLabel}
                  >
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        gap: 8
                      }}
                    >
                      <span
                        style={{
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        {baseLabel}
                      </span>
                      {categoryName && (
                        <Tag
                          color='blue'
                          style={{ marginRight: 0, flexShrink: 0 }}
                        >
                          {categoryName}
                        </Tag>
                      )}
                    </div>
                  </Option>
                )
              })}
            </Select>
          </Form.Item>

          <Form.Item
            name='quantity'
            label='數量'
            rules={[
              { required: true, message: '請輸入數量' },
              {
                type: 'number',
                min: 0.01,
                message: '數量必須大於 0'
              },
              {
                validator: (_, v) => {
                  const stock = selectedProduct?.stock ?? 0
                  if (v != null && Number(v) > stock) {
                    return Promise.reject(
                      new Error(
                        `數量不能超過目前庫存（${stock} ${selectedProduct?.unit || '件'}）`
                      )
                    )
                  }
                  return Promise.resolve()
                }
              }
            ]}
            extra={
              selectedProduct ? (
                <div>
                  {selectedPerUnitSpec ? (
                    <div
                      style={{
                        fontWeight: 600,
                        color: '#374151',
                        marginBottom: 4
                      }}
                    >
                      {selectedPerUnitSpec}
                      <span
                        style={{
                          fontWeight: 400,
                          color: '#6b7280',
                          marginLeft: 6
                        }}
                      >
                        （數量以「{selectedProduct.unit || '件'}」為準，可填小數）
                      </span>
                    </div>
                  ) : null}
                  <div>
                    目前庫存：{selectedProduct.stock}{' '}
                    {selectedProduct.unit || '件'}
                    （可填整數或小數，例：0.5）
                  </div>
                </div>
              ) : (
                '請先選擇商品'
              )
            }
          >
            <InputNumber
              style={{ width: '100%' }}
              size='large'
              min={0.01}
              max={selectedProduct?.stock || undefined}
              step={0.5}
              precision={2}
              addonAfter={selectedProduct?.unit || '件'}
              // 用 parent state 而不是 useWatch 來判斷是否禁用，避免 form 剛 mount
              // 時 useWatch 還沒同步造成「灰一下」
              disabled={!productSelected}
              placeholder='輸入數量（例 0.5、1、1.5）'
              onChange={(value) => {
                // value 可能是 null（使用者清空輸入框）；
                // 直接寫進 state，不要強制變 0，讓欄位可以保持空白等使用者重打。
                handleProductChange(item.key, 'quantity', value)
                lastProcessedQuantityRef.current = null
              }}
            />
          </Form.Item>
        </FormRow>

        {canManageInventory && (
          <FormRow>
            <Form.Item
              name='cost'
              label='成本（單價）'
              rules={[
                { required: true, message: '請輸入成本' },
                {
                  type: 'number',
                  min: 0,
                  message: '成本必須大於等於0'
                }
              ]}
            >
              <InputNumber
                style={{ width: '100%' }}
                size='large'
                min={0}
                prefix='NT$'
                onChange={(value) => {
                  const costValue = value || 0
                  handleProductChange(item.key, 'cost', costValue)
                  lastProcessedCostRef.current = null
                }}
              />
            </Form.Item>

            <Form.Item name='totalCost' label='總成本'>
              <InputNumber
                style={{ width: '100%' }}
                size='large'
                prefix='NT$'
                disabled
                readOnly
              />
            </Form.Item>
          </FormRow>
        )}

        {selectedProduct && (
          <div
            style={{
              marginTop: 8,
              padding: 10,
              background: '#f9fafb',
              borderRadius: 6,
              fontSize: 12,
              color: '#6b7280'
            }}
          >
            {selectedPerUnitSpec ? (
              <div
                style={{
                  marginBottom: 6,
                  fontWeight: 600,
                  color: '#111827',
                  fontSize: 13
                }}
              >
                {selectedPerUnitSpec}
              </div>
            ) : null}
            {selectedProduct.manufacturer && (
              <div style={{ marginBottom: 4 }}>
                廠商：{selectedProduct.manufacturer}
              </div>
            )}
            {selectedProduct.stock !== undefined && (
              <div
                style={{
                  color:
                    selectedProduct.stock <=
                    (selectedProduct.lowStockThreshold || 10)
                      ? '#ef4444'
                      : '#10b981'
                }}
              >
                庫存：{selectedProduct.stock}
                {selectedProduct.unit || '件'}
              </div>
            )}
          </div>
        )}
      </Form>
    </ProductItemCard>
  )
}

const InventoryPublicUsePage = () => {
  const { user } = useAuth()
  const navigate = useNavigate()
  const { hasFeature } = useMembership()
  const { can } = usePermission()
  const isMobile = useMediaQuery({ maxWidth: 768 })
  const [form] = Form.useForm()
  const [loading, setLoading] = useState(false)
  const [products, setProducts] = useState([])
  const [categories, setCategories] = useState([])
  const [productItems, setProductItems] = useState([])
  const productFormsRef = useRef({})
  // 商品篩選狀態：分類 + 關鍵字（套用到所有商品列共用的下拉清單）
  const [filterCategoryId, setFilterCategoryId] = useState('all')
  const [filterKeyword, setFilterKeyword] = useState('')

  const canViewFinance = can('finance:view')
  // 只有老闆/店長（inventory:manage）可以看到並編輯「成本（單價）」與總成本
  // 一般員工（僅 inventory:view）也能執行拆公用，但成本一律沿用商品設定值
  const canManageInventory =
    can('inventory:manage') || can('inventory:*') || can('*')

  // 套用「分類 + 關鍵字」篩選；給所有 ProductItemForm 共用
  const visibleProducts = (() => {
    const keyword = (filterKeyword || '').trim().toLowerCase()
    return products.filter((product) => {
      const categoryId =
        product.categoryId || product.category?.id || product.category?._id
      const categoryMatch =
        filterCategoryId === 'all' || categoryId === filterCategoryId
      if (!categoryMatch) return false
      if (!keyword) return true
      const name = (product.name || product.title || '').toLowerCase()
      const manufacturer = (product.manufacturer || '').toLowerCase()
      return name.includes(keyword) || manufacturer.includes(keyword)
    })
  })()

  useEffect(() => {
    if (!hasFeature('inventory')) return
    fetchProducts()
    fetchCategories()
    // 設置默認日期為今天
    form.setFieldsValue({
      date: tzNow(),
      time: tzNow().format('HH:mm')
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const fetchProducts = async () => {
    if (!user?.businessId) return
    try {
      const response = await getInventoryProducts({
        page: 1,
        pageSize: 1000,
        status: 'active' // 只獲取啟用的商品
      })
      const productsData =
        response.data?.data?.items || response.data?.items || []
      setProducts(productsData)
    } catch (error) {
      console.error('獲取商品列表失敗:', error)
      //   message.error('獲取商品列表失敗')
    }
  }

  const fetchCategories = async () => {
    if (!user?.businessId) return
    try {
      const response = await getInventoryCategories()
      let categoriesData = []
      if (Array.isArray(response.data?.data)) {
        categoriesData = response.data.data
      } else if (Array.isArray(response.data)) {
        categoriesData = response.data
      } else if (
        response.data &&
        typeof response.data === 'object' &&
        response.data.code === 200
      ) {
        categoriesData = Array.isArray(response.data.data)
          ? response.data.data
          : []
      }
      setCategories(categoriesData)
    } catch (error) {
      //console.error('獲取分類列表失敗:', error)
    }
  }

  // 計算總成本
  const totalCost = productItems.reduce((sum, item) => {
    return sum + (Number(item.totalCost) || 0)
  }, 0)

  // 添加商品項目
  const handleAddProduct = () => {
    const newKey = `product-${Date.now()}`
    setProductItems([
      ...productItems,
      {
        key: newKey,
        productId: null,
        quantity: 1,
        cost: 0,
        totalCost: 0
      }
    ])
  }

  // 刪除商品項目
  const handleRemoveProduct = (key) => {
    setProductItems(productItems.filter((item) => item.key !== key))
    delete productFormsRef.current[key]
  }

  // 更新商品項目
  const handleProductChange = (key, field, value) => {
    setProductItems((prevItems) => {
      const updatedItems = prevItems.map((item) => {
        if (item.key === key) {
          const updatedItem = { ...item, [field]: value }

          // 如果選擇了商品，自動填充成本
          if (field === 'productId' && value) {
            const product = products.find(
              (p) => p._id === value || p.id === value
            )
            if (product) {
              updatedItem.cost = product.cost || 0
              updatedItem.productName = product.name || product.title
              updatedItem.unit = product.unit || '件'
              updatedItem.stock = product.stock || 0
            }
          }

          // 如果數量或成本改變，重新計算總成本
          if (field === 'quantity' || field === 'cost') {
            updatedItem.totalCost =
              (Number(updatedItem.quantity) || 0) *
              (Number(updatedItem.cost) || 0)
          }

          return updatedItem
        }
        return item
      })
      return updatedItems
    })
  }

  // 提交表單
  const handleSubmit = async () => {
    try {
      // 驗證表單
      const values = await form.validateFields()

      // 驗證商品項目
      if (productItems.length === 0) {
        message.error('請至少添加一個商品')
        return
      }

      // 驗證每個商品項目
      for (const item of productItems) {
        if (!item.productId) {
          message.error('請選擇所有商品')
          return
        }
        if (!item.quantity || item.quantity <= 0) {
          message.error('請輸入有效的數量')
          return
        }

        // 檢查庫存
        const product = products.find(
          (p) => p._id === item.productId || p.id === item.productId
        )
        if (product && item.quantity > (product.stock || 0)) {
          message.error(
            `商品「${item.productName}」庫存不足，目前庫存：${
              product.stock || 0
            } ${product.unit || '件'}`
          )
          return
        }
      }

      setLoading(true)

      // 構建請求數據
      const requestData = {
        type: 'public_use',
        items: productItems.map((item) => {
          const product = products.find(
            (p) => p._id === item.productId || p.id === item.productId
          )
          const itemData = {
            productId: item.productId,
            productName: item.productName || product?.name || product?.title,
            quantity: Number(item.quantity)
          }

          // 老闆/店長（inventory:manage）可改成本，使用使用者輸入的值
          // 一般員工：固定使用商品建檔的成本，不接受使用者輸入
          if (canManageInventory) {
            itemData.cost = Number(item.cost) || product?.cost || 0
            itemData.totalCost = Number(item.totalCost) || 0
          } else {
            itemData.cost = product?.cost || 0
            itemData.totalCost =
              (Number(item.quantity) || 0) * (product?.cost || 0)
          }

          return itemData
        }),
        date: values.date
          ? dayjs(values.date).format('YYYY-MM-DD')
          : tzNow().format('YYYY-MM-DD'),
        time: values.time || tzNow().format('HH:mm'),
        date: values.date
          ? dayjs(values.date).format('YYYY-MM-DD')
          : tzNow().format('YYYY-MM-DD'),
        time: values.time || tzNow().format('HH:mm'),
        note: values.note || ''
      }

      // 調用API創建使用記錄
      const response = await createInventoryUsage(requestData)

      if (response.data?.success !== false) {
        message.success('拆公用記錄創建成功')
        // 重置表單
        form.resetFields()
        setProductItems([])
        // 設置默認日期為今天
        form.setFieldsValue({
          date: tzNow(),
          time: tzNow().format('HH:mm')
        })
        // 刷新商品列表（更新庫存）
        fetchProducts()
      } else {
        message.error(response.data?.message || '創建失敗')
      }
    } catch (error) {
      console.error('提交失敗:', error)
      if (error.errorFields && error.errorFields.length > 0) {
        // 表單驗證錯誤
        return
      }
      message.error(
        error.response?.data?.message ||
          error.message ||
          '創建拆公用記錄失敗，請稍後再試'
      )
    } finally {
      setLoading(false)
    }
  }

  // 權限檢查
  if (!hasFeature('inventory')) {
    return (
      <PageContainer>
        <Empty description='此功能需要庫存管理權限' />
      </PageContainer>
    )
  }

  return (
    <PageContainer>
      <PageHeader>
        <Breadcrumb
          items={[
            {
              title: (
                <span>
                  <HomeOutlined /> 首頁
                </span>
              ),
              href: '/appointments'
            },
            {
              title: (
                <span>
                  <ShoppingOutlined /> 庫存管理
                </span>
              )
            },
            {
              title: '拆公用'
            }
          ]}
          style={{ marginBottom: 16 }}
        />
        <PageTitle>拆公用</PageTitle>
      </PageHeader>

      <FormCard title='拆公用記錄'>
        <Form form={form} layout='vertical'>
          <FormRow>
            <Form.Item
              name='date'
              label='使用日期'
              rules={[{ required: true, message: '請選擇使用日期' }]}
            >
              <DatePicker
                style={{ width: '100%' }}
                size='large'
                format='YYYY-MM-DD'
                placeholder='選擇日期'
                inputReadOnly={isMobile}
                getPopupContainer={(trigger) => trigger.parentElement}
              />
            </Form.Item>

            <Form.Item
              name='time'
              label='使用時間'
              rules={[{ required: true, message: '請輸入使用時間' }]}
            >
              <Input
                size='large'
                placeholder='例如：14:30'
                maxLength={5}
                onInput={(e) => {
                  // 格式化時間輸入（HH:mm）
                  let value = e.target.value.replace(/[^\d:]/g, '')
                  if (value.length === 2 && !value.includes(':')) {
                    value = value + ':'
                  }
                  if (value.length > 5) {
                    value = value.slice(0, 5)
                  }
                  e.target.value = value
                }}
              />
            </Form.Item>
          </FormRow>

          <Form.Item name='note' label='備註'>
            <TextArea
              rows={3}
              placeholder='請輸入備註（可選）'
              maxLength={500}
              showCount
            />
          </Form.Item>
        </Form>

        <div style={{ marginTop: 24, marginBottom: 16 }}>
          <Row gutter={[12, 12]} align='middle' wrap>
            <Col xs={24} sm={12} md={8}>
              <Select
                value={filterCategoryId}
                onChange={(value) => setFilterCategoryId(value)}
                style={{ width: '100%' }}
                size='large'
                suffixIcon={<FilterOutlined />}
                placeholder='依分類篩選商品'
              >
                <Option value='all'>全部分類</Option>
                {categories.map((cat) => (
                  <Option key={cat._id} value={cat._id}>
                    {cat.name}
                  </Option>
                ))}
              </Select>
            </Col>
            <Col xs={24} sm={12} md={8}>
              <Input
                allowClear
                size='large'
                placeholder='搜尋商品名稱或廠商'
                value={filterKeyword}
                onChange={(e) => setFilterKeyword(e.target.value)}
                prefix={<FilterOutlined style={{ color: '#bbb' }} />}
              />
            </Col>
            <Col xs={24} md={8}>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  gap: 12,
                  flexWrap: 'wrap'
                }}
              >
                <Text type='secondary' style={{ fontSize: 12 }}>
                  {filterCategoryId === 'all' && !filterKeyword
                    ? `共 ${products.length} 項商品`
                    : `已篩選：${visibleProducts.length} / ${products.length} 項`}
                </Text>
                <Button
                  type='primary'
                  icon={<PlusOutlined />}
                  onClick={handleAddProduct}
                  size='large'
                >
                  添加商品
                </Button>
              </div>
            </Col>
          </Row>
        </div>

        {productItems.map((item) => (
          <ProductItemForm
            key={item.key}
            item={item}
            products={products}
            visibleProducts={visibleProducts}
            categories={categories}
            handleProductChange={handleProductChange}
            handleRemoveProduct={handleRemoveProduct}
            productFormsRef={productFormsRef}
            canManageInventory={canManageInventory}
          />
        ))}

        {productItems.length === 0 && (
          <div
            style={{
              textAlign: 'center',
              padding: '40px 20px',
              color: '#999',
              background: '#f9fafb',
              borderRadius: 8,
              border: '1px dashed #d1d5db'
            }}
          >
            <ShoppingOutlined style={{ fontSize: 48, marginBottom: 16 }} />
            <div>請點擊「添加商品」按鈕添加要公用的商品</div>
          </div>
        )}

        {productItems.length > 0 && canManageInventory && (
          <SummaryCard>
            <div className='summary-title'>總成本</div>
            <div className='summary-value'>
              NT$ {totalCost.toLocaleString()}
            </div>
          </SummaryCard>
        )}

        <ButtonGroup>
          <Button
            size='large'
            onClick={() => {
              form.resetFields()
              setProductItems([])
              form.setFieldsValue({
                date: tzNow(),
                time: tzNow().format('HH:mm')
              })
            }}
          >
            重置
          </Button>
          <Button
            type='primary'
            size='large'
            icon={<SaveOutlined />}
            onClick={handleSubmit}
            loading={loading}
            disabled={productItems.length === 0}
          >
            提交記錄
          </Button>
        </ButtonGroup>
      </FormCard>
    </PageContainer>
  )
}

export default InventoryPublicUsePage
